import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../api';
import type { BmaAnnahme, BmaBefund, BmaFachfrage, BmaKapitel } from './elektro/bma';
import { PDF_HINWEIS as HEFT_HINWEIS, alsVorbehalte, auswahlParameter } from './elektro/bma';
import type { StNotstrom } from './elektro/sicherheitstechnik';
import { statusText, zerlegeBefund } from './elektro/sicherheitstechnik';
import type {
  SaaLautsprecher, SaaLinie, SaaNotstromteile, SaaStand, SaaVerstaerker, SaaZone,
} from './elektro/sprachalarm';
import { TEILUNG_HINWEIS, FESTLEGUNG_ALARMIERUNG, saaEinzelausgabe, saaRechenstand } from './elektro/sprachalarm';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, ExportLeiste, Feld, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, formatiereZahl, type ExportZiel, type Spalte, type Zustand,
} from '../muster';

/**
 * Seite „Sprachalarmanlage" (W2-D2, 16.10.2026; seit SA-1 vom 20.10.2026 nach TX-STANDARD S1).
 *
 * LIEST `GET …/elektro/sicherheitstechnik/saa`: Rechenstand, Vorbehalte, die Kapitel des Hefts ELT-SAH,
 * 100-V-Lautsprecherlinien, Verstärkerbilanz mit Kanalbelegung, Zonen je Abschnitt, Notstrom der SAA-Zentralen, die
 * Lautsprecherliste, Annahmen und Vorgaben mit Herkunft, Fachfragen und Befunde. Gerechnet wird auf dem Server
 * (Saastand), die Seite schreibt nichts. Die Vorbehalte stehen wortgleich im Vorbehaltskasten des Hefts.
 *
 * Ausgaben (TX-STANDARD S1.9):
 *   - PDF Gesamtbericht: Heft ELT-SAH mit allen Kapiteln;
 *   - Kapitel wählen …: ein Schubfach mit Haken je Kapitel, daraus ein Teilheft (dieselbe Art, der Gegenstand nennt
 *     die Auswahl); je Kapitel auch einzeln in der Kapiteltabelle. Kapitel mit eigener Berichtsart (ELT-SPA, ELT-LSL)
 *     liefern diese Art.
 *   - Excel: SAA-Listen über den Excel-Kern.
 * Jeder PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung an, sonst die abgelegte (F11).
 *
 * `reiter` nimmt die LocalTabs der Sicherheitstechnik auf (SicherheitstechnikSeiten.tsx); sie stehen im Seitenkopf.
 */
export function SprachalarmAnsicht({ api, projektId, reiter }: { api: ApiClient; projektId: string; reiter?: ReactNode }) {
  const [stand, setStand] = useState<SaaStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schubfach, setSchubfach] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [einzelFehler, setEinzelFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const s = await api.elektroSaa(projektId);
      setStand(s ?? null);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const waehlbar = useMemo(() => (stand?.kapitel ?? []).filter((k) => k.art === 'wahl'), [stand]);
  const parameter = stand ? auswahlParameter(gewaehlt, stand.kapitel) : '';

  async function kapitelLaden(k: BmaKapitel) {
    setEinzelFehler(null);
    try {
      switch (saaEinzelausgabe(k)) {
        case 'spa': await api.elektroSaaNachweisLaden(projektId); break;
        case 'lsl': await api.elektroSaaListeLaden(projektId); break;
        case 'teilheft': await api.elektroSaaBerichtLaden(projektId, k.nummer); break;
        default: break;
      }
    } catch (e: unknown) {
      setEinzelFehler(e instanceof Error ? e.message : String(e));
    }
  }

  const ziele: ExportZiel[] = [
    { art: 'pdf', beschriftung: 'PDF Gesamtbericht', laden: () => api.elektroSaaBerichtLaden(projektId), hinweis: HEFT_HINWEIS },
    { art: 'excel', beschriftung: 'Excel SAA-Listen', laden: () => api.elektroSaaListen(projektId) },
  ];

  const aktionen = (
    <>
      <ExportLeiste ziele={ziele} />
      <button type="button" className="m-knopf" onClick={() => setSchubfach(true)} disabled={!stand}>
        Kapitel wählen …
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Sprachalarmanlage"
        beschreibung="Zentralen, Lautsprecher, Zonen, 100-V-Linien, Verstärker, Notstrom und Ansteuerung der Sprachalarmanlage nach DIN VDE 0833-4 aus einem Bestand – mit Gesamtbericht und Kapitelwahl als PDF. Nur lesend."
        meta={stand ? saaRechenstand(stand.staende) : 'noch nicht geladen'}
        aktionen={aktionen}
      >
        {reiter}
      </PageHeader>
      {fehler && (
        <ErrorState titel="Die Sprachalarmanlage konnte nicht geladen werden." technisch={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      )}
      {!fehler && !stand && <LoadingState />}
      {stand && stand.kennzahlen.zentralen === 0 && (
        <EmptyState titel="Keine SAA-Zentrale angelegt"
                    erklaerung="SAA-Zentralen und Lautsprecherlinien entstehen über „Meldelinien“ (Zentralen erkennen, Linien bilden)." />
      )}
      {stand && stand.kennzahlen.zentralen > 0 && (
        <Inhalt stand={stand} projektId={projektId} beiKapitel={(k) => { void kapitelLaden(k); }} einzelFehler={einzelFehler} />
      )}
      <Drawer offen={schubfach} titel="Kapitel wählen" beiSchliessen={() => setSchubfach(false)}
              beschreibung="Das Teilheft enthält die gewählten Kapitel und immer die Pflichtkapitel 1 Grundlagen und Normbezug, 2 Annahmen und Vorgaben, 12 Befunde und 13 Offene Fachfragen."
              fuss={(
                <ExportLeiste ziele={[{
                  art: 'pdf', beschriftung: 'PDF der Auswahl', hinweis: HEFT_HINWEIS,
                  laden: () => api.elektroSaaBerichtLaden(projektId, parameter),
                  gesperrt: parameter === '' ? { grund: 'Mindestens ein Kapitel wählen.' } : undefined,
                }]} />
              )}>
        <Feld art="mehrfachauswahl" beschriftung="Kapitel" breite="voll" wert={gewaehlt}
              optionen={waehlbar.map((k) => ({ wert: k.nummer, beschriftung: `${k.nummer} ${k.titel}` }))}
              beiAenderung={(w) => setGewaehlt(Array.isArray(w) ? (w as string[]) : [])}
              hinweis={parameter ? `Auswahl: ${parameter.split(',').join(', ')}` : 'Noch kein Kapitel gewählt.'} />
      </Drawer>
    </div>
  );
}

function rangZustand(rang: string): Zustand {
  return rang === 'Fehler' ? 'fehler' : rang === 'Warnung' ? 'warnung' : 'info';
}

function zustandVon(status: string): Zustand {
  return status === 'fehler' ? 'fehler' : status === 'warnung' ? 'warnung' : 'gut';
}

function Statusmarke({ status, gegenAnnahmen }: { status: string; gegenAnnahmen: boolean }) {
  return <StatusBadge zustand={zustandVon(status)} text={statusText(status, gegenAnnahmen)} klein />;
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${stufe}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

function Inhalt({ stand, projektId, beiKapitel, einzelFehler }: {
  stand: SaaStand; projektId: string; beiKapitel: (k: BmaKapitel) => void; einzelFehler: string | null;
}) {
  const k = stand.kennzahlen;
  const u = stand.uebersicht;
  return (
    <>
      <Vorbehalte eintraege={alsVorbehalte(stand.vorbehalte)} />
      <KpiReihe>
        <KpiCard titel="Lautsprecher" wert={k.lautsprecher}
                 vergleich={`Leistung aus Name ${k.leistungAusName} · Vorgabe ${k.leistungAusVorgabe}`} />
        <KpiCard titel="Leistung" wert={Math.round(k.leistungW)} einheit="W" />
        <KpiCard titel="Linien" wert={k.linien} vergleich={`${k.abschnitte} Abschnitte`} />
        <KpiCard titel="ΔU über Grenze" wert={k.linienUeberSpannungsfall}
                 zustand={k.linienUeberSpannungsfall > 0 ? 'warnung' : 'gut'}
                 zustandText={k.linienUeberSpannungsfall > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="A/B gemischt" wert={k.linienStraengeGemischt}
                 zustand={k.linienStraengeGemischt > 0 ? 'warnung' : 'gut'}
                 zustandText={k.linienStraengeGemischt > 0 ? 'neu bilden' : 'getrennt'} />
        <KpiCard titel="Befunde" wert={u.warnungen + u.fehler} vergleich={`${u.fehler} Fehler, ${u.warnungen} Warnungen`}
                 zustand={u.fehler > 0 ? 'fehler' : u.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={u.fehler > 0 ? 'beheben' : u.warnungen > 0 ? 'prüfen' : 'keine'} />
      </KpiReihe>
      <Kapitel kapitel={stand.kapitel} projektId={projektId} beiKapitel={beiKapitel} fehler={einzelFehler} />
      <Verstaerker zeilen={stand.verstaerker} />
      <Linien zeilen={stand.linien} />
      <Zonen zeilen={stand.zonen} />
      <Notstrom zeilen={stand.notstrom} teile={stand.notstromTeile ?? []} />
      <Lautsprecherliste zeilen={stand.lautsprecher} />
      <Annahmen zeilen={stand.annahmen} />
      <Fachfragen zeilen={stand.fachfragen} />
      <Befunde zeilen={stand.befunde} />
    </>
  );
}

function Kapitel({ kapitel, projektId, beiKapitel, fehler }: {
  kapitel: BmaKapitel[]; projektId: string; beiKapitel: (k: BmaKapitel) => void; fehler: string | null;
}) {
  const spalten: Spalte<BmaKapitel>[] = [
    { schluessel: 'nummer', titel: 'Nr.', wert: (z) => z.nummer, immerSichtbar: true },
    { schluessel: 'titel', titel: 'Kapitel', wert: (z) => z.titel, immerSichtbar: true },
    {
      schluessel: 'art', titel: 'Art', art: 'status', wert: (z) => z.art,
      inhalt: (z) => (z.art === 'pflicht' ? <StatusBadge zustand="neutral" text="Pflicht" klein />
        : z.art === 'gesamt' ? <StatusBadge zustand="neutral" text="nur Gesamtbericht" klein />
          : <StatusBadge zustand="info" text="wählbar" klein />),
    },
    { schluessel: 'einzel', titel: 'Einzelausgabe', wert: (z) => (z.art === 'wahl' ? (z.einzelausgabe ?? 'Teilheft ELT-SAH') : '—') },
    { schluessel: 'befunde', titel: 'Befunde', art: 'zahl', wert: (z) => z.befunde },
    { schluessel: 'quelle', titel: 'Quelle', wert: (z) => z.quelle, versteckt: true },
    {
      schluessel: 'seite', titel: 'Seite', wert: (z) => z.seite ?? '',
      inhalt: (z) => (z.seite ? <Link to={`/projekte/${projektId}/${z.seite}`}>öffnen</Link> : '—'),
    },
    {
      schluessel: 'pdf', titel: 'PDF', wert: () => '', sortierbar: false,
      inhalt: (z) => (z.art === 'wahl'
        ? (
          <button type="button" className="m-knopf" title={HEFT_HINWEIS}
                  aria-label={`Kapitel ${z.nummer} ${z.titel} als PDF`} onClick={() => beiKapitel(z)}>
            PDF
          </button>
        )
        : '—'),
    },
  ];
  return (
    <ContentSection titel="Kapitel des Hefts ELT-SAH"
                    beschreibung="Jedes wählbare Kapitel einzeln als PDF: mit eigener Berichtsart (ELT-SPA, ELT-LSL) deren Fassung, sonst ein Teilheft mit den Pflichtkapiteln. Mehrere Kapitel über „Kapitel wählen …“.">
      {fehler && <ErrorState titel="Das Kapitel konnte nicht erzeugt werden." technisch={fehler} klein />}
      <DataTable name={`Kapitel (${kapitel.length})`} spalten={spalten} zeilen={kapitel}
                 schluessel={(z) => z.nummer} suche={false} dicht />
    </ContentSection>
  );
}

function Verstaerker({ zeilen }: { zeilen: SaaVerstaerker[] }) {
  const spalten: Spalte<SaaVerstaerker>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'linien', titel: 'Linien', art: 'zahl', wert: (z) => z.linien },
    { schluessel: 'p', titel: 'P', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.leistungW },
    { schluessel: 'kanal', titel: 'Kanal', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.kanalleistungW },
    { schluessel: 'kanaele', titel: 'Kanäle erf.', art: 'zahl', wert: (z) => z.kanaeleErforderlich },
    { schluessel: 'einheiten', titel: 'Einheiten erf.', art: 'zahl', wert: (z) => z.einheitenErforderlich },
    { schluessel: 'ersatz', titel: 'mit Ersatz', art: 'zahl', wert: (z) => z.einheitenMitErsatz },
    { schluessel: 'verbaut', titel: 'verbaut', art: 'zahl', wert: (z) => z.einheitenVerbaut },
    { schluessel: 'kreise', titel: 'Kreise max.', art: 'zahl', wert: (z) => z.kreiseMax },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="Verstärkerbilanz"
                    beschreibung="Linien absteigend nach Leistung in Kanäle zu Kanalleistung · Ausnutzung gepackt; Einheiten nach Kanälen und Gesamtleistung, dazu der Ersatzverstärker.">
      <DataTable name={`SAA-Zentralen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.zentrale} suche={false} dicht />
      {zeilen.map((z) => (
        <div key={z.zentrale} className="karte">
          <h3>{z.zentrale}</h3>
          <p className="klein">{z.rechenweg.join(' · ')}</p>
          <p className="klein">{z.kanalbelegung.map((c) => `K${c.nummer} ${Math.round(c.leistungW)} W (${c.linien.join(', ')})`).join(' · ')}</p>
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Linien({ zeilen }: { zeilen: SaaLinie[] }) {
  const [nurBefund, setNurBefund] = useState(true);
  const [gewaehlt, setGewaehlt] = useState<SaaLinie | null>(null);
  const gezeigt = nurBefund ? zeilen.filter((z) => z.status !== 'ok') : zeilen;
  const spalten: Spalte<SaaLinie>[] = [
    { schluessel: 'linie', titel: 'Linie', wert: (z) => z.linie },
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'ls', titel: 'Lautsprecher', art: 'zahl', wert: (z) => z.lautsprecher },
    { schluessel: 'p', titel: 'P', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.leistungW },
    { schluessel: 'l', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.laengeM },
    { schluessel: 'a', titel: 'A', einheit: 'mm²', art: 'zahl', nachkomma: 3, wert: (z) => z.querschnittMm2 },
    { schluessel: 'du', titel: 'ΔU', einheit: '%', art: 'zahl', nachkomma: 1, wert: (z) => z.spannungsfallProzent },
    {
      schluessel: 'du1', titel: 'ΔU bei k = 1,0', einheit: '%', art: 'zahl', nachkomma: 1,
      wert: (z) => z.spannungsfallProzentLastEnde ?? null,
      titelHinweis: 'Empfindlichkeit: alle Lautsprecher am Linienende. Ist nur dieser Wert über der Grenze, ist das eine Warnung.',
    },
    { schluessel: 'dl', titel: 'ΔL', einheit: 'dB', art: 'zahl', nachkomma: 1, wert: (z) => z.pegelverlustDb },
    { schluessel: 'lmax', titel: 'L max', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.laengeMaxM },
    { schluessel: 'aerf', titel: 'A erf.', einheit: 'mm²', art: 'zahl', nachkomma: 2, wert: (z) => z.querschnittErforderlichMm2 },
    {
      schluessel: 'teile', titel: 'Teile', art: 'zahl', wert: (z) => z.teileObereSchranke ?? null,
      titelHinweis: TEILUNG_HINWEIS,
    },
    { schluessel: 'ab', titel: 'A / B / A-B', wert: (z) => `${z.strangA} / ${z.strangB} / ${z.strangAb}` },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="Lautsprecherlinien (100 V)"
                    beschreibung={`I = P / U; ΔU = k · I · 2ρL/A mit k = 0,5 (gleichmäßig verteilt), dazu ΔU bei k = 1,0 als Empfindlichkeit; ΔL = 20 · lg(U_Ende / U). Fernmeldeleitungen nach Aderdurchmesser (A = π d²/4). Teile: ${TEILUNG_HINWEIS}. Eine Zeile anklicken zeigt Rechenweg und Befunde.`}>
      <label className="klein">
        <input type="checkbox" checked={nurBefund} onChange={(e) => setNurBefund(e.target.checked)} />
        {' '}nur Linien mit Befund ({zeilen.filter((z) => z.status !== 'ok').length} von {zeilen.length})
      </label>
      <DataTable name={`Linien (${gezeigt.length})`} spalten={spalten} zeilen={gezeigt}
                 schluessel={(z) => z.linie} beiZeileKlick={(z) => setGewaehlt(z)} dicht />
      {gewaehlt && (
        <div className="karte">
          <h3>{gewaehlt.linie}</h3>
          <p className="klein">{gewaehlt.leitungsart ?? '—'} · {gewaehlt.querschnittHerleitung ?? 'Leiter unbekannt'}</p>
          {gewaehlt.rechenweg && <p className="klein">{gewaehlt.rechenweg}</p>}
          <p className="klein">Abschnitte: {gewaehlt.abschnitte.join(', ') || '—'} · Funktionserhalt: {gewaehlt.funktionserhalt ?? '—'}</p>
          <Befundliste befunde={gewaehlt.befunde} />
        </div>
      )}
    </ContentSection>
  );
}

/**
 * SA-3: die Zonen mit Herkunft - gepflegtes Feld „beschallungszone“, sonst der Abschnitt aus Quadrant und Geschoss -
 * und dem Vorbehalt der Alarmierung, wörtlich wie im Heft.
 */
export function Zonen({ zeilen }: { zeilen: SaaZone[] }) {
  const spalten: Spalte<SaaZone>[] = [
    { schluessel: 'zone', titel: 'Zone', wert: (z) => z.name },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => z.herkunft },
    { schluessel: 'ls', titel: 'Lautsprecher', art: 'zahl', wert: (z) => z.lautsprecher },
    { schluessel: 'p', titel: 'P', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.leistungW },
    { schluessel: 'a', titel: 'A', art: 'zahl', wert: (z) => z.strangA },
    { schluessel: 'b', titel: 'B', art: 'zahl', wert: (z) => z.strangB },
    { schluessel: 'ab', titel: 'A/B', art: 'zahl', wert: (z) => z.strangAb },
    { schluessel: 'ohne', titel: 'ohne', art: 'zahl', wert: (z) => z.strangOhne },
    { schluessel: 'linien', titel: 'Linien', wert: (z) => z.linien.join(', ') },
    { schluessel: 'befund', titel: 'Befund', wert: (z) => z.befunde.map((b) => zerlegeBefund(b).text).join(' · ') },
  ];
  return (
    <ContentSection titel="Zonen und Alarmierung"
                    beschreibung={`Zone = gepflegtes Feld „beschallungszone“, sonst Abschnitt aus Quadrant und Geschoss (Annahme BMA-02), eine Zone je Brandabschnitt (SAA-04b). ${FESTLEGUNG_ALARMIERUNG}: alle Zonen gleichzeitig.`}>
      <DataTable name={`Zonen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.name} dicht />
    </ContentSection>
  );
}

function zahl(x: number, nachkomma = 0): string {
  return formatiereZahl(x, nachkomma);
}

function Aufschluesselung({ t }: { t: SaaNotstromteile }) {
  const vergleich = t.vergleich.map((v) => `${zahl(v.ruhezeitH)} h: ${zahl(v.kapazitaetAh)} Ah`).join(' · ');
  const ersatz = t.davonErsatz > 0 ? ` (davon ${t.davonErsatz} Ersatz)` : '';
  const ruhe = `Ruhe: ${zahl(t.zentraleRuheA, 2)} A Zentrale + ${t.verstaerkereinheiten} Verstärkereinheiten${ersatz}`
    + ` × ${zahl(t.verstaerkerRuheJeEinheitW)} W = ${zahl(t.ruhestromA, 2)} A (${zahl(t.ruheleistungW)} W)`
    + ` × ${zahl(t.ruhezeitH)} h = ${zahl(t.ruheAh)} Ah (${zahl(t.ruheanteilProzent)} %)`;
  const alarm = `Alarm: Endstufen ${zahl(t.lautsprecherleistungW)} W × ${zahl(t.gleichzeitigkeit, 2)}`
    + ` × ${zahl(t.alarmleistungFaktor, 2)} / ${zahl(t.wirkungsgrad, 2)} = ${zahl(t.endstufenAlarmW)} W;`
    + ` I_A = ${zahl(t.alarmstromA, 2)} A × ${zahl(t.alarmzeitH, 1)} h = ${zahl(t.alarmAh)} Ah`;
  const summe = `C = (${zahl(t.ruheAh)} + ${zahl(t.alarmAh)}) Ah × ${zahl(t.zuschlag, 2)} = ${zahl(t.kapazitaetAh)} Ah`
    + ` (${zahl(t.energieKWh, 1)} kWh) · bei Überbrückungszeit ${vergleich}`;
  return (
    <ul className="liste klein">
      <li>{ruhe}</li>
      <li>{alarm}</li>
      <li>{summe}</li>
    </ul>
  );
}

function Notstrom({ zeilen, teile }: { zeilen: StNotstrom[]; teile: SaaNotstromteile[] }) {
  const spalten: Spalte<StNotstrom>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'ir', titel: 'I_R', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.ruhestromA },
    { schluessel: 'ia', titel: 'I_A', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.alarmstromA },
    { schluessel: 'c', titel: 'C erforderlich', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.kapazitaetErforderlichAh },
    { schluessel: 'cb', titel: 'C verbaut', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.bestandAh },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="Notstrom der SAA-Zentralen"
                    beschreibung="C = (I_R · t_R + I_A · t_A) · f; im Alarm die Endstufen mit P · g · f_Alarm / (η · U). * = gegen unbestätigte Vorgaben gerechnet.">
      <DataTable name={`Zentralen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.zentrale} suche={false} dicht />
      {zeilen.map((z) => {
        const t = teile.find((x) => x.zentrale === z.zentrale);
        return (
          <div key={z.zentrale} className="karte">
            <h3>{z.zentrale}</h3>
            {t && <Aufschluesselung t={t} />}
            <Befundliste befunde={z.befunde} />
          </div>
        );
      })}
      <p className="klein">Überbrückungszeit 72 h nach FLB (ELT-70); mit Ersatzstromversorgung oder kurzfristiger
        Störungsbehebung sind nach DIN VDE 0833-1/-4 kürzere Zeiten möglich — daher der Vergleich. f_Alarm 1,0 =
        Alarmsignal mit voller Leistung über die ganze Alarmzeit (konservativ).</p>
    </ContentSection>
  );
}

/** SA-1: die Lautsprecherliste - derselbe Stand und dieselbe Ordnung wie ELT-LSL und das Blatt „Lautsprecher“. */
function Lautsprecherliste({ zeilen }: { zeilen: SaaLautsprecher[] }) {
  const spalten: Spalte<SaaLautsprecher>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'linie', titel: 'Linie', wert: (z) => z.linie, immerSichtbar: true },
    { schluessel: 'nummer', titel: 'Nummer', wert: (z) => z.nummer ?? '—', immerSichtbar: true },
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ ?? z.familie ?? '—' },
    { schluessel: 'strang', titel: 'Strang', wert: (z) => z.strang ?? '—' },
    { schluessel: 'p', titel: 'P', einheit: 'W', art: 'zahl', nachkomma: 1, wert: (z) => z.leistungW },
    { schluessel: 'herkunft', titel: 'Herkunft P', wert: (z) => z.leistungHerkunft },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raum ?? '—' },
    { schluessel: 'raumname', titel: 'Raumname', wert: (z) => z.raumname ?? '—' },
    { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (z) => z.abschnitt },
  ];
  return (
    <ContentSection titel="Lautsprecherliste"
                    beschreibung="Alle Lautsprecher an SAA-Linien, geordnet nach Zentrale, Linie und Nummer – wie ELT-LSL und das Blatt „Lautsprecher“ der SAA-Listen. Leistung gepflegt, aus dem Namen oder aus der Vorgabe; der Abschnitt aus Quadrant und Geschoss.">
      <DataTable name={`Lautsprecher (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.zentrale}|${z.linie}|${z.nummer ?? ''}|${z.raum ?? ''}`} dicht ausfuhr
                 ausfuhrDateiname="saa-lautsprecher.csv"
                 leer={<EmptyState titel="Keine Lautsprecher" erklaerung="An den SAA-Linien hängt noch kein Lautsprecher." />} />
    </ContentSection>
  );
}

function Annahmen({ zeilen }: { zeilen: BmaAnnahme[] }) {
  const spalten: Spalte<BmaAnnahme>[] = [
    { schluessel: 'parameter', titel: 'Parameter', wert: (z) => z.parameter, immerSichtbar: true },
    { schluessel: 'wert', titel: 'Wert', wert: (z) => z.wert ?? '—' },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit ?? '' },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => z.herkunft },
    { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (z) => [z.quelle, z.normbezug].filter(Boolean).join(' · ') },
    {
      schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status', wert: (z) => z.bestaetigt,
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'bestätigt' : 'unbestätigt'} klein />,
    },
  ];
  const geordnet = [...zeilen].sort((a, b) => Number(a.bestaetigt) - Number(b.bestaetigt));
  return (
    <ContentSection titel="Annahmen und Vorgaben"
                    beschreibung="Dieselbe Liste wie das Annahmenblatt des Hefts (Kapitel 2) und das Blatt „Annahmen“ der SAA-Listen; unbestätigte zuerst, je Vorgabe mit Herkunft und Migration.">
      <DataTable name={`Annahmen (${zeilen.length})`} spalten={spalten} zeilen={geordnet}
                 schluessel={(z) => z.parameter} dicht ausfuhr ausfuhrDateiname="saa-annahmen.csv"
                 leer={<EmptyState titel="Keine Annahme" erklaerung="Für dieses Projekt ist keine Vorgabe der SAA angelegt." />} />
    </ContentSection>
  );
}

function Fachfragen({ zeilen }: { zeilen: BmaFachfrage[] }) {
  const spalten: Spalte<BmaFachfrage>[] = [
    { schluessel: 'nummer', titel: 'Nr.', wert: (z) => z.nummer, immerSichtbar: true },
    { schluessel: 'prio', titel: 'Prio', wert: (z) => z.prio },
    {
      schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={z.antwort ? 'gut' : 'warnung'} text={z.antwort ? 'beantwortet' : 'offen'} klein />,
    },
    { schluessel: 'frage', titel: 'Frage', wert: (z) => z.frage },
    { schluessel: 'antwort', titel: 'Antwort', wert: (z) => z.antwort ?? '—' },
    { schluessel: 'kapitel', titel: 'Kapitel', wert: (z) => z.kapitel },
    { schluessel: 'quelle', titel: 'Quelle', wert: (z) => z.quelle, versteckt: true },
  ];
  return (
    <ContentSection titel="Fachfragen" beschreibung="Fachfragen mit Bezug zur Sprachalarmanlage – offen und beantwortet, mit den Kapiteln, deren Ergebnis daran hängt.">
      <DataTable name={`Fachfragen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.nummer} dicht
                 leer={<EmptyState titel="Keine Fachfrage" erklaerung="Zur Sprachalarmanlage ist keine Fachfrage offen." />} />
    </ContentSection>
  );
}

function Befunde({ zeilen }: { zeilen: BmaBefund[] }) {
  const spalten: Spalte<BmaBefund>[] = [
    {
      schluessel: 'rang', titel: 'Rang', art: 'status', wert: (z) => z.rang,
      inhalt: (z) => <StatusBadge zustand={rangZustand(z.rang)} text={z.rang} klein />,
    },
    { schluessel: 'regel', titel: 'Regel', wert: (z) => z.regel },
    { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (z) => z.gegenstand },
    { schluessel: 'meldung', titel: 'Meldung', wert: (z) => z.meldung, immerSichtbar: true },
    { schluessel: 'kapitel', titel: 'Kapitel', wert: (z) => z.kapitel },
  ];
  return (
    <ContentSection titel="Befunde" beschreibung="Fehler zuerst, dann Warnungen und Hinweise – dieselbe Liste wie Kapitel 12 des Hefts.">
      <DataTable name={`Befunde (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.kapitel}|${z.regel}|${z.gegenstand}|${z.meldung}`} dicht ausfuhr
                 ausfuhrDateiname="saa-befunde.csv"
                 leer={<EmptyState titel="Keine Befunde" erklaerung="Der Stand der Sprachalarmanlage ergibt keinen Befund." />} />
    </ContentSection>
  );
}
